import { clsx } from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'

type UseThumbsType = {
  selectedIndex: number
  onThumbClick: (index: number) => void
}

export const useThumbs = (
  emblaMainApi: EmblaCarouselType | undefined,
  emblaThumbsApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
): UseThumbsType => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return
      emblaMainApi.scrollTo(index)

      if (onButtonClick) onButtonClick(emblaMainApi)
    },
    [emblaMainApi, emblaThumbsApi, onButtonClick]
  )

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return
    setSelectedIndex(emblaMainApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap())
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaMainApi) return
    onSelect()
    emblaMainApi.on('select', onSelect)
    emblaMainApi.on('reInit', onSelect)
  }, [emblaMainApi, onSelect])

  return {
    selectedIndex,
    onThumbClick,
  }
}

type PropType = {
  selected: boolean
  title?: string
  img: { src: string; alt: string }
  index: number
  onClick: () => void
}

export const ThumbButton: React.FC<PropType> = props => {
  const { selected, title, img, onClick } = props

  return (
    <div className="shrink-0 grow-0 basis-[66%] md:basis-[42%] lg:basis-[27%] 2xl:basis-[21%] px-3 lg:px-6">
      <button
        onClick={onClick}
        className={clsx('w-full h-0 relative pb-[67%] border-2 rounded-lg', {
          'border-candle-yellow': selected,
          'border-transparent': !selected,
        })}
        type="button"
      >
        <img
          className="absolute top-0 left-0 h-full w-full object-cover rounded-lg"
          src={img?.src}
          alt={img?.alt}
        />
      </button>
      {title && (
        <p className="font-bold text-dark-grey text-left pt-3">{title}</p>
      )}
    </div>
  )
}
